import { Dayjs } from "dayjs";

/**
 * The days an FSM/CRM has declared full.
 *
 * They reach the widget as one comma delimited string on a channel action response -
 * never on the initial render - and each entry is either a "YYYY-MM-DD" date, a weekday
 * name as `dddd` formats it ("Saturday"), or either of those with `*` standing in for any
 * run of characters ("2026-08-*").
 */

/**
 * Split what the channel sent into the entries to match against.
 *
 * Reports `undefined` rather than an empty array when nothing has arrived, so a caller
 * can tell "no availability information yet" from "nothing is busy".
 *
 * @param busyDays The comma delimited list, as the channel sends it.
 */
export function parseBusyDays(busyDays?: string): string[] | undefined {
    if (!busyDays) {
        return undefined;
    }

    const entries = busyDays
        .split(",")
        .map((entry) => entry.trim())
        .filter((entry) => !!entry);

    return entries.length > 0 ? entries : undefined;
}

/**
 * Whether the CRM has declared the given day full.
 *
 * An absent or empty list means nothing is busy, so a picker shows its own availability
 * rather than greying everything or nothing out while the response is still in flight.
 *
 * @param day The day being drawn.
 * @param busyDays The entries from {@link parseBusyDays}.
 */
export function isBusyDay(day: Dayjs, busyDays?: readonly string[]): boolean {
    if (!busyDays || busyDays.length === 0) {
        return false;
    }

    const dateString = day.format("YYYY-MM-DD");
    const dayString = day.format("dddd");

    return busyDays.some((busyDay) => isMatch(dateString, busyDay) || isMatch(dayString, busyDay));
}

// Replace * with a regex that matches any number of characters
function isMatch(str: string, pattern: string): boolean {
    const regexPattern = pattern.split("*").map(escapeRegExp).join(".*");
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(str);
}

function escapeRegExp(str: string): string {
    return str.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
}
